import type { ScreenId } from "../nav";

export type LazyRouteRecoveryTarget = {
  projectId: string;
  screen: Extract<ScreenId, "quote" | "spec">;
  apartmentTypeId?: string | undefined;
  specTab?: "prod" | "schedules" | undefined;
};

export type LazyRouteRecoverySurfaceName = "Products" | "Schedules" | "Quote";

export type ConsumedLazyRouteRecovery = {
  target: LazyRouteRecoveryTarget | null;
  surfaceName: LazyRouteRecoverySurfaceName;
  source: "url" | "session" | "memory";
};

type LazyRouteRecoveryPayload = {
  createdAt: number;
  error: true;
  surfaceName: LazyRouteRecoverySurfaceName;
  target?: LazyRouteRecoveryTarget | undefined;
};

type StoredLazyRouteRecoveryTarget = LazyRouteRecoveryTarget & {
  createdAt: number;
};

const recoveryKey = "tcs.lazyRouteRecovery.v1";
export const lazyRouteRecoveryUrlParam = "tcsLazyRouteRecovery";
const recoveryMaxAgeMs = 5 * 60 * 1000;
let inMemoryRecovery: LazyRouteRecoveryPayload | null = null;
let reloadInFlight = false;

const storage = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

const isLazyRouteRecoveryTarget = (
  value: unknown
): value is LazyRouteRecoveryTarget => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<LazyRouteRecoveryTarget>;

  return (
    typeof candidate.projectId === "string" &&
    candidate.projectId.length > 0 &&
    (candidate.screen === "quote" || candidate.screen === "spec") &&
    (candidate.apartmentTypeId === undefined ||
      typeof candidate.apartmentTypeId === "string") &&
    (candidate.specTab === undefined ||
      candidate.specTab === "prod" ||
      candidate.specTab === "schedules")
  );
};

const isRecoverySurfaceName = (
  value: unknown
): value is LazyRouteRecoverySurfaceName =>
  value === "Products" || value === "Schedules" || value === "Quote";

const targetFrom = (target: LazyRouteRecoveryTarget): LazyRouteRecoveryTarget => ({
  projectId: target.projectId,
  screen: target.screen,
  ...(target.apartmentTypeId ? { apartmentTypeId: target.apartmentTypeId } : {}),
  ...(target.specTab ? { specTab: target.specTab } : {})
});

const surfaceNameForTarget = (
  target: LazyRouteRecoveryTarget
): LazyRouteRecoverySurfaceName =>
  target.screen === "quote"
    ? "Quote"
    : target.specTab === "schedules"
      ? "Schedules"
      : "Products";

const isFresh = (createdAt: unknown): createdAt is number =>
  typeof createdAt === "number" && Date.now() - createdAt <= recoveryMaxAgeMs;

const payloadForTarget = (
  target: LazyRouteRecoveryTarget
): LazyRouteRecoveryPayload => ({
  createdAt: Date.now(),
  error: true,
  surfaceName: surfaceNameForTarget(target),
  target: targetFrom(target)
});

const recoveryFromParsed = (
  parsed: unknown,
  source: ConsumedLazyRouteRecovery["source"]
): ConsumedLazyRouteRecovery | null => {
  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const candidate = parsed as Partial<
    LazyRouteRecoveryPayload & StoredLazyRouteRecoveryTarget
  >;

  if (!isFresh(candidate.createdAt)) {
    return null;
  }

  if (isLazyRouteRecoveryTarget(candidate)) {
    const target = targetFrom(candidate);

    return {
      target,
      surfaceName: surfaceNameForTarget(target),
      source
    };
  }

  if (candidate.error !== true || !isRecoverySurfaceName(candidate.surfaceName)) {
    return null;
  }

  if (candidate.target !== undefined && !isLazyRouteRecoveryTarget(candidate.target)) {
    return {
      target: null,
      surfaceName: candidate.surfaceName,
      source
    };
  }

  const target = candidate.target ? targetFrom(candidate.target) : null;

  return {
    target,
    surfaceName: target ? surfaceNameForTarget(target) : candidate.surfaceName,
    source
  };
};

const recoveryFromRaw = (
  raw: string | null,
  source: ConsumedLazyRouteRecovery["source"]
): ConsumedLazyRouteRecovery | null => {
  if (!raw) {
    return null;
  }

  try {
    return recoveryFromParsed(JSON.parse(raw), source);
  } catch {
    return null;
  }
};

const consumeSessionRecovery = (): ConsumedLazyRouteRecovery | null => {
  const session = storage();

  if (!session) {
    return null;
  }

  try {
    const raw = session.getItem(recoveryKey);
    session.removeItem(recoveryKey);

    return recoveryFromRaw(raw, "session");
  } catch {
    return null;
  }
};

const consumeUrlRecovery = (): ConsumedLazyRouteRecovery | null => {
  let raw: string | null = null;

  try {
    const url = new URL(globalThis.window.location.href);

    raw = url.searchParams.get(lazyRouteRecoveryUrlParam);

    if (raw === null) {
      return null;
    }

    url.searchParams.delete(lazyRouteRecoveryUrlParam);

    try {
      globalThis.window.history.replaceState(
        globalThis.window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch {
      // Best-effort cleanup: recovery should still happen even if history is locked.
    }
  } catch {
    return null;
  }

  return recoveryFromRaw(raw, "url");
};

const consumeInMemoryRecovery = (): ConsumedLazyRouteRecovery | null => {
  const recovery = inMemoryRecovery;

  inMemoryRecovery = null;

  return recoveryFromParsed(recovery, "memory");
};

export const consumeLazyRouteRecovery = (): ConsumedLazyRouteRecovery | null => {
  const urlRecovery = consumeUrlRecovery();
  const sessionRecovery = consumeSessionRecovery();

  return sessionRecovery ?? urlRecovery ?? consumeInMemoryRecovery();
};

const persistSessionRecovery = (payload: LazyRouteRecoveryPayload): boolean => {
  const session = storage();

  if (!session) {
    return false;
  }

  try {
    session.setItem(recoveryKey, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
};

const replaceLocationWithRecovery = (
  payload: LazyRouteRecoveryPayload
): boolean => {
  try {
    const url = new URL(globalThis.window.location.href);

    url.searchParams.set(lazyRouteRecoveryUrlParam, JSON.stringify(payload));
    globalThis.window.location.replace(url.toString());
    return true;
  } catch {
    return false;
  }
};

export const reloadForLazyRouteRecovery = (
  target: LazyRouteRecoveryTarget
): void => {
  if (reloadInFlight) {
    return;
  }

  reloadInFlight = true;

  const payload = payloadForTarget(target);
  const sessionPersisted = persistSessionRecovery(payload);

  if (replaceLocationWithRecovery(payload)) {
    return;
  }

  if (sessionPersisted) {
    try {
      globalThis.window.location.reload();
      return;
    } catch {
      // Fall through to the in-memory guard below.
    }
  }

  inMemoryRecovery = payload;
};
